import clsx from "clsx";
import { FunctionComponent, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { ZEPHR_CLIFF_STAND_IN_CSS } from "./zephr-cliff-stand-in-css";

/** Matches `Paywall.scripts` after `Zephr.openFooter()`. */
const ZEPHR_CLIFF_OPEN_DELAY_MS = 1000;

/**
 * Markup + CSS matching production `#zephr-zone-footer`. Opens when
 * `#zephr-anchor` intersects the viewport (Zephr’s injection site), then waits
 * 1s before translating the cliff, showing the overlay, and locking scroll.
 */
const CskPaywallCliffStandIn: FunctionComponent = () => {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [cliffOpen, setCliffOpen] = useState(false);

  useEffect(() => {
    setRoot(document.body);
  }, []);

  useEffect(() => {
    if (!root) {
      return;
    }

    const anchor = document.getElementById("zephr-anchor");
    if (!anchor) {
      return;
    }

    let timeoutId: number | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }
        observer.disconnect();
        timeoutId = window.setTimeout(() => {
          setCliffOpen(true);
        }, ZEPHR_CLIFF_OPEN_DELAY_MS);
      },
      { threshold: 0 },
    );
    observer.observe(anchor);

    return () => {
      observer.disconnect();
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [root]);

  useEffect(() => {
    if (!cliffOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [cliffOpen]);

  if (!root) {
    return null;
  }

  return createPortal(
    <>
      <div
        id="zephr-overlay"
        className={clsx(cliffOpen && "visible")}
        data-csk-preview-stand-in="paywall-overlay"
      />
      <div
        className={clsx("zephr-zone-footer", cliffOpen && "transform")}
        id="zephr-zone-footer"
        data-csk-preview-stand-in="paywall"
        data-open-after="-1"
        data-scrollable="false"
        style={{ display: "block" }}
      >
        <style>{ZEPHR_CLIFF_STAND_IN_CSS}</style>
        <div
          id="zephr-footer-container"
          className={clsx(cliffOpen && "visible")}
          data-brandid="1209"
          data-wid="content cliff - csk stand-in"
        >
          <div id="zephr-footer-body">
            <div id="zephr-footer-body-section-1">
              <span id="zephr-footer-body-title">
                Continue reading with a Verge subscription
              </span>
              <span id="zephr-footer-body-subheading">
                Unlock unlimited access to The Verge for just $2.
              </span>
            </div>
            <div id="zephr-footer-body-section-2">
              <button id="zephr-footer-body-button" type="button">
                START YOUR TRIAL
              </button>
              <div id="zephr-footer-bottom">
                <span>
                  <span className="zephr-footer-text">
                    Already a subscriber?
                  </span>{" "}
                  <span
                    id="zephr-link-1"
                    className="zephr-footer-text zephr-link"
                  >
                    Sign in
                  </span>
                </span>
                <span id="zephr-link-divider" />
                <span>
                  <span className="zephr-footer-text">Back to</span>{" "}
                  <span
                    id="zephr-link-2"
                    className="zephr-footer-text zephr-link"
                  >
                    The Verge Homepage
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    root,
  );
};

export default CskPaywallCliffStandIn;
